package guide.kuki.app.engine

import kotlin.math.*

const val EARTH_RADIUS = 6378137.0 // in metres

fun deg2rad(deg: Double): Double = deg * PI / 180.0

/**
 * Haversine distance between two [lat, lon] points, in metres.
 */
fun distance(latlon1: DoubleArray, latlon2: DoubleArray): Double {
    val dLat = deg2rad(latlon2[0] - latlon1[0])
    val dLon = deg2rad(latlon2[1] - latlon1[1])
    val a = sin(dLat / 2).pow(2) +
            cos(deg2rad(latlon1[0])) * cos(deg2rad(latlon2[0])) * sin(dLon / 2).pow(2)
    val c = 2 * atan2(sqrt(a), sqrt(1 - a))
    return EARTH_RADIUS * c
}

/**
 * 2D cross product (outer product) of vectors (xy - xy1) and (xy2 - xy1).
 * Positive when xy is to the right of line xy1→xy2.
 */
fun outerProduct(xy: DoubleArray, xy1: DoubleArray, xy2: DoubleArray): Double {
    return (xy[0] - xy1[0]) * (xy2[1] - xy1[1]) - (xy[1] - xy1[1]) * (xy2[0] - xy1[0])
}

fun toRightOfLine(xy: DoubleArray, xy1: DoubleArray, xy2: DoubleArray): Boolean {
    return outerProduct(xy, xy1, xy2) >= 0.0
}

fun toLeftOfLine(xy: DoubleArray, xy1: DoubleArray, xy2: DoubleArray): Boolean {
    return !toRightOfLine(xy, xy1, xy2)
}

/**
 * Linear interpolation between two [lat, lon] points.
 * xi=0 returns pt0, xi=1 returns pt1.
 */
fun interpolateXi(xi: Double, pt0: DoubleArray, pt1: DoubleArray): DoubleArray {
    val phi0 = 1 - xi
    val phi1 = xi
    return doubleArrayOf(phi0 * pt0[0] + phi1 * pt1[0], phi0 * pt0[1] + phi1 * pt1[1])
}
